/**
 * Lap Mappers
 *
 * Bi-directional conversion between Lap domain model and LapDto
 */

import { Lap } from '../../domain/lap/Lap'
import { buildLap } from '../../domain/lap/buildLap'
import { LapDto, TelemetryDto } from '../apiTypes'
import {
  processRawTelemetry,
  TelemetryProcessorOptions,
} from '../../processors/rawTelemetryProcessor'
import { rawTelemetryToDto, telemetryDtoToRaw } from './telemetryMapper'
import { RawTelemetryData } from '../../domain/telemetry/raw/RawTelemetryData'
import { checkGpsReadings } from '../../telemetryChecks/gpsVerdicts'

/**
 * Convert Lap domain model to LapDto for API calls
 *
 * This is used when saving lap data to the backend.
 *
 * Telemetry is optional. Pass it explicitly when the DTO must carry its own
 * telemetry (e.g. sharing a single lap). Omit it when the telemetry is
 * already being sent elsewhere in the request (e.g. saving a whole track
 * session, where per-lap telemetry would otherwise duplicate the
 * session-level telemetry payload for every lap).
 *
 * @param lap - The Lap domain model
 * @param telemetry - Optional telemetry DTO (convert from RawTelemetryData using rawTelemetryToDto)
 * @returns LapDto ready for API submission; `telemetry` is omitted when not provided
 */
export function lapToDto(lap: Lap, telemetry?: TelemetryDto): LapDto {
  return {
    id: lap.id,
    lapNumber: lap.lapNumber,
    startTime: lap.startTime,
    finishTime: lap.finishTime,
    lapTimeMs: lap.lapTime,
    trackSessionId: lap.sessionId,
    trackId: lap.trackId,
    weather: lap.weather,
    driverName: lap.driverName,
    ...(telemetry !== undefined ? { telemetry } : {}),
  }
}

/**
 * Convert a Lap's own telemetry to a TelemetryDto ready to share, carrying
 * the session-scope diagnostics (IMU lag, gyro lean-rate bias) the lap was
 * itself processed with, so a later `lapDtoToLap` applies rather than
 * re-measures them.
 *
 * @param lap - The Lap domain model, with its raw and processed telemetry
 * @returns TelemetryDto for this lap's own telemetry, `diagnostics` included only when there is something to write
 */
export function lapTelemetryToDto(lap: Lap): TelemetryDto {
  if (!lap.hasReadings) {
    throw new Error(
      `Lap ${lap.id} carries no readings to share; rebuild it from its session first`
    )
  }
  return rawTelemetryToDto(
    lap.rawTelemetry,
    lap.processedTelemetryData.diagnostics
  )
}

function withCoordinateDerivedDistance(
  rawTelemetry: RawTelemetryData
): RawTelemetryData {
  return {
    ...rawTelemetry,
    gps: rawTelemetry.gps.map(
      ({ distanceMeters: _stored, ...reading }) => reading
    ),
  }
}

/**
 * Convert a LapDto (with telemetry) to a Lap domain model.
 *
 * The DTO's telemetry is processed like a one-lap session and the lap is
 * assembled by the same buildLap every other entry point uses.
 *
 * @param lapDto - The LapDto from the API (must include telemetry)
 * @returns Lap domain model ready for analysis, or null if telemetry is missing
 */
function sessionScopedOptions(
  telemetry: NonNullable<LapDto['telemetry']>
): TelemetryProcessorOptions {
  const options: TelemetryProcessorOptions = {}
  const diagnostics = telemetry.diagnostics
  if (typeof diagnostics?.imuLagMs === 'number')
    options.imuLagMs = diagnostics.imuLagMs
  if (typeof diagnostics?.gyroLeanRateBiasDegS === 'number')
    options.gyroLeanRateBiasDegS = diagnostics.gyroLeanRateBiasDegS
  return options
}

export function lapDtoToLap(lapDto: LapDto): Lap | null {
  if (!lapDto.telemetry) {
    return null
  }

  const startTime = new Date(lapDto.startTime)
  const rawTelemetry = withCoordinateDerivedDistance(
    telemetryDtoToRaw(lapDto.telemetry, startTime)
  )
  const sessionTelemetry = processRawTelemetry(
    rawTelemetry,
    rawTelemetry.recordingStartTime ?? startTime,
    {
      ...sessionScopedOptions(lapDto.telemetry),
      gpsVerdicts: checkGpsReadings(rawTelemetry.gps).verdicts,
    }
  )

  const lap = buildLap({
    metadata: {
      lapId: lapDto.id,
      lapNumber: lapDto.lapNumber,
      driverName: lapDto.driverName ?? 'Unknown',
      trackSessionId: lapDto.trackSessionId ?? '',
      trackId: lapDto.trackId,
    },
    window: { startTime, finishTime: new Date(lapDto.finishTime) },
    sessionTelemetry,
    sessionRawTelemetry: rawTelemetry,
    device: lapDto.telemetry.device,
    weather: lapDto.weather,
  })
  if (!lap) return null

  lap.isSelected = true
  lap.isShared = true
  return lap
}
